import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import axios from 'axios';

const ItineraryDownload = ({ charterNumber, charterStatus, onClose }) => {
  const [itineraryStatus, setItineraryStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchItineraryStatus();
  }, [charterNumber]);

  const fetchItineraryStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`/api/itineraries/status/${charterNumber}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setItineraryStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch itinerary status:', error);
      setError(error.response?.data?.message || 'Failed to fetch itinerary status');
    } finally {
      setLoading(false);
    }
  };

  const generateItinerary = async () => {
    try {
      setGenerating(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`/api/itineraries/generate/${charterNumber}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh status after generation
      await fetchItineraryStatus();
      
      // Auto-download the generated PDF
      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Failed to generate itinerary:', error);
      setError(error.response?.data?.message || 'Failed to generate itinerary');
    } finally {
      setGenerating(false);
    }
  };

  const downloadItinerary = () => {
    if (itineraryStatus?.itineraryInfo?.downloadUrl) {
      window.open(itineraryStatus.itineraryInfo.downloadUrl, '_blank');
    }
  };

  // Allow generation for CREW_ASSIGNMENT or later (backend will validate sector status)
  const canGenerateItinerary = ['CREW_ASSIGNMENT', 'READY_FOR_BA', 'READY_FOR_ATC', 'COMPLETED', 'CHARTER_COMPLETED'].includes(charterStatus);
  const hasItinerary = itineraryStatus?.hasItinerary;

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PdfIcon color="primary" />
          <Typography variant="h6">
            Itinerary - {charterNumber}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && itineraryStatus && (
          <Box>
            {/* Charter Status */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Charter Status
              </Typography>
              <Chip 
                label={itineraryStatus.status} 
                color={itineraryStatus.status === 'COMPLETED' ? 'success' : 'warning'}
                size="small"
              />
            </Box>

            {/* Itinerary Status */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Itinerary Status
              </Typography>
              {hasItinerary ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label="Generated" 
                    color="success"
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Generated on {new Date(itineraryStatus.itineraryInfo.generatedAt).toLocaleDateString()}
                  </Typography>
                </Box>
              ) : (
                <Chip 
                  label="Not Generated" 
                  color="default"
                  size="small"
                />
              )}
            </Box>

            {/* Actions */}
            <Box sx={{ mt: 3 }}>
              {!canGenerateItinerary && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Itinerary can only be generated after crew assignment (Step 5) and when at least one sector has accepted pilots.
                </Alert>
              )}

              {canGenerateItinerary && !hasItinerary && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={generating ? <CircularProgress size={20} /> : <PdfIcon />}
                  onClick={generateItinerary}
                  disabled={generating}
                  sx={{ mb: 2 }}
                >
                  {generating ? 'Generating Itinerary...' : 'Generate Itinerary'}
                </Button>
              )}

              {hasItinerary && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={downloadItinerary}
                  sx={{ mb: 2 }}
                >
                  Download Itinerary
                </Button>
              )}

              {hasItinerary && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={generateItinerary}
                  disabled={generating}
                >
                  {generating ? 'Regenerating...' : 'Regenerate Itinerary'}
                </Button>
              )}
            </Box>

            {/* Sample Preview */}
            <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Itinerary Preview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The generated itinerary will include:
              </Typography>
              <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                <Typography component="li" variant="body2" color="text.secondary">
                  Charter details and passenger information
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Flight route and timing details
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Aircraft and crew information
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Terms and conditions
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Professional formatting with company branding
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItineraryDownload;
